import { queryKeys } from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  humanizeStatus,
  SUPPORT_TICKET_STATUS_TONE,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Headset, Plus } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { CHANNEL_LABEL, NewTicketDialog } from '../routes/support-tickets.js'
import { ExpandToggle, TicketNotesPreview } from './notes-preview.js'

interface TicketRow {
  id: string
  ticketNumber: string
  subject: string
  channel: string
  status: string
  lastMessageAt: string
}

/**
 * Support tickets linked to one customer (t1218), shown on their page. Every
 * status is listed — a closed ticket is still part of the history — newest
 * activity first, as the API returns them. "New" opens the new-ticket dialog
 * with this customer already filled in.
 */
export function CustomerSupportTicketsCard({
  customer,
}: {
  customer: { id: string; name: string }
}) {
  const { can } = useAuth()
  const canRead = can('support:read')
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const filters = { customerId: customer.id, pageSize: 50 }
  const { data } = useQuery({
    queryKey: queryKeys.supportTickets.list(filters),
    queryFn: () => api.get<{ items: TicketRow[] }>('/support-tickets', { query: filters }),
    enabled: canRead,
    refetchInterval: 15_000,
  })

  if (!canRead) return null
  const items = data?.items ?? []

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Headset className="size-4" aria-hidden="true" /> Support tickets
        </CardTitle>
        {can('support:write') ? (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            <Plus /> New
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {items.length ? (
          <ul className="divide-y divide-border">
            {items.map((ticket) => (
              <li key={ticket.id}>
                <div className="flex items-center hover:bg-surface-sunken">
                  <span className="ps-4">
                    <ExpandToggle
                      expanded={expanded.has(ticket.id)}
                      onToggle={() => toggleExpanded(ticket.id)}
                      label={ticket.ticketNumber}
                    />
                  </span>
                  <Link
                    to="/support-tickets/$ticketId"
                    params={{ ticketId: ticket.id }}
                    className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-sm"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{ticket.subject}</span>
                      <span className="block font-identifier text-2xs text-foreground-muted">
                        {ticket.ticketNumber} · {CHANNEL_LABEL[ticket.channel] ?? ticket.channel} ·{' '}
                        {new Date(ticket.lastMessageAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </span>
                    <Badge tone={SUPPORT_TICKET_STATUS_TONE[ticket.status] ?? 'neutral'}>
                      {humanizeStatus(ticket.status)}
                    </Badge>
                  </Link>
                </div>
                {expanded.has(ticket.id) ? (
                  <div className="bg-surface-sunken/40 px-4 py-3">
                    <TicketNotesPreview ticketId={ticket.id} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6">
            <EmptyState
              icon={Headset}
              title="No support tickets"
              description="Calls, texts and tickets linked to them appear here."
            />
          </div>
        )}
      </CardContent>
      <NewTicketDialog open={adding} onOpenChange={setAdding} customer={customer} />
    </Card>
  )
}
